import { AffectedDiagnostic, ChatWidget, HpCompleteComponentV10, QiraMessageContentChunk, Source, VariantGroupsWithComponents } from 'api3';
import { Injectable, inject } from '@angular/core';
import { Api3Service } from '@app/services/api3.service';
import { QiraSubject, SourceUrl } from './types';
import { toError } from './error-normalization-utils';
import * as Sentry from '@sentry/angular';

export enum MessageType {
  Text = 'text',
  Document = 'document',
  Sources = 'sources',
  Widget = 'widget',
}

export const isGuidedDiagnosisWidget = (
  widget: ChatWidget,
): widget is ChatWidget & { id: 'guided-diagnosis'; parameters: { carTypeId: number; errorCode: string } } => {
  return widget.id === 'guided-diagnosis' && widget.parameters?.carTypeId !== undefined && widget.parameters?.errorCode !== undefined;
};

export type systemGroupData = {
  systemGroupId?: number | undefined;
  systemGroupName: string;
};

export type DiagnosisParams = {
  carTypeId: number;
  errorCode?: string;
  tabId: string;
  variantGroupId?: number;
  systemGroupId?: number;
  systemGroupName?: string;
  systemGroups?: systemGroupData[];
  variantId?: number;
  componentId?: number;
  componentIds?: number[];
  isExplorerMode?: boolean;
  selectedSystemGroupName?: string;
  provider?: Source['provider'];
};

export type QiraSystemGroup = {
  id: string;
  type: MessageType;
  subject: Source['subject'] | undefined;
  name: string;
  text: string;
  link?: SourceUrl;
  provider?: Source['provider'];
  componentData?: any;
  cited?: boolean;
};

@Injectable({ providedIn: 'root' })
export class MessageService {
  api3Service = inject(Api3Service);

  async getWidgetGroups(content: QiraMessageContentChunk | undefined) {
    const widgetType = content?.widget?.id;
    if (widgetType === 'guided-diagnosis' && content?.widget) {
      const carTypeId = content?.widget?.parameters?.carTypeId;
      const errorCode = content?.widget?.parameters?.errorCode;
      if (!carTypeId || !errorCode) return [];
      const variants = await this.getVariantGroupsWithComponents(carTypeId, errorCode);
      const groups: QiraSystemGroup[] = [];
      variants.variantGroups.forEach((variantGroup) => {
        if (!variantGroup.systemGroups) {
          groups.push({
            id: String(variantGroup.system_group_id),
            type: MessageType.Widget,
            subject: this.getMessageSubject(content),
            name: variantGroup.text ?? '',
            text: variantGroup.text ?? '',
            provider: content?.provider,
            componentData: variantGroup,
          });
        }
        variantGroup.systemGroups?.forEach((systemGroup) => {
          groups.push({
            id: `${systemGroup.system_group_id ?? 0}-${variantGroup.system_group_number ?? 0}`,
            type: MessageType.Widget,
            subject: this.getMessageSubject(content),
            name: systemGroup.text ?? '',
            text: systemGroup.text ?? '',
            provider: content?.provider,
            componentData: systemGroup,
          });
        });
      });
      return groups;
    }

    if (widgetType === 'component-wiring-diagram') {
      const components = (content?.widget?.parameters?.components || []).reduce((acc: QiraSystemGroup[], component: any) => {
        const systemId = component.systemId ? String(component.systemId) : Math.random().toString(36).substring(2, 10);
        const systemIndex = acc.findIndex((group) => group.id === systemId);
        if (systemIndex >= 0) {
          acc[systemIndex].componentData.componentData.push(component);
        } else {
          acc.push({
            id: component.systemId ? String(component.systemId) : Math.random().toString(36).substring(2, 10),
            type: MessageType.Widget,
            subject: this.getMessageSubject(content),
            name: component.systemName,
            text: component.systemName,
            provider: content?.provider,
            componentData: {
              systemGroupId: null,
              systemGroupName: component.systemName,
              componentData: [component],
            },
          } as QiraSystemGroup);
        }
        return acc;
      }, []);

      const systemGroups = (content?.widget?.parameters?.systemGroups || []).map((systemGroup) => ({
        id: systemGroup.systemGroupId ? String(systemGroup.systemGroupId) : Math.random().toString(36).substring(2, 10),
        type: MessageType.Widget,
        subject: this.getMessageSubject(content),
        name: systemGroup.systemGroupName,
        text: systemGroup.systemGroupName,
        provider: content?.provider,
        componentData: systemGroup,
      }));

      return [...components, ...systemGroups];
    }
    return [];
  }

  getDiagnosisLink(linkData: DiagnosisParams): { path: string; params: Record<string, string> } {
    const stringParams: Record<string, string> = {
      carTypeId: String(linkData.carTypeId),
      diagnosisView: 'true',
    };
    if (linkData.errorCode) stringParams['errorCode'] = linkData.errorCode;
    if (linkData.variantGroupId) stringParams['variantGroupId'] = String(linkData.variantGroupId);
    if (linkData.systemGroupId) stringParams['systemGroupId'] = String(linkData.systemGroupId);
    if (linkData.systemGroupName) stringParams['systemGroupName'] = linkData.systemGroupName;
    if (linkData.variantId) stringParams['variantId'] = String(linkData.variantId);
    if (linkData.componentId) stringParams['componentId'] = String(linkData.componentId);
    if (linkData.componentIds) stringParams['componentIds'] = linkData.componentIds.join(',');
    if (linkData.provider) stringParams['provider'] = linkData.provider;

    return {
      path: `/chat/${linkData.tabId}/`,
      params: stringParams,
    };
  }

  getMessageType(content: QiraMessageContentChunk | undefined) {
    if (content?.document) {
      return MessageType.Document;
    } else if (content?.sources) {
      return MessageType.Sources;
    } else if (content?.text) {
      return MessageType.Text;
    } else if (content?.widget) {
      return MessageType.Widget;
    } else {
      return undefined;
    }
  }

  getMessageSubject(content: QiraMessageContentChunk | undefined): Source['subject'] | undefined {
    const messageType = this.getMessageType(content);

    if (messageType === MessageType.Document) {
      return content?.document?.subject || undefined;
    } else if (messageType === MessageType.Widget) {
      const isGuidedDiagnosis = content?.widget && isGuidedDiagnosisWidget(content.widget);
      const isWiringDiagram = content?.widget && content.widget.id === 'component-wiring-diagram';
      if (isGuidedDiagnosis) return QiraSubject.WiringDiagrams;
      if (isWiringDiagram) return QiraSubject.WiringDiagrams;
      return undefined;
    }
    return undefined;
  }

  async getVariantGroupsWithComponents(carTypeId: number, errorCode: string): Promise<VariantGroupsWithComponents> {
    const { data, error } = await this.api3Service.api.rmi.getVariantGroupsWithComponentsForErrorCode(carTypeId, errorCode);
    if (error) {
      console.error('[MessageService] Error fetching variant groups with components:', error);
      return { variantGroups: [], diagnosticComponents: null };
    }
    return data || { variantGroups: [], diagnosticComponents: null };
  }

  async getComponents(carTypeId: number, componentIds: number[]): Promise<VariantGroupsWithComponents> {
    const { data, error } = await this.api3Service.api.rmi.getComponentsByIds({
      carTypeId,
      componentIds,
    });

    if (error) {
      console.error('[MessageService] Error fetching variant groups:', error);
      return { variantGroups: [], diagnosticComponents: null };
    }

    return data;
  }

  async getCompleteDiagnosticComponent(
    carTypeId: number,
    symbolInstanceId: number,
    systemTypeId: number,
  ): Promise<HpCompleteComponentV10 | undefined> {
    const { data, error } = await this.api3Service.api.rmi.getCompleteDiagnosticComponent(carTypeId, symbolInstanceId, systemTypeId);
    if (error) {
      console.error('[MessageService] Error fetching complete diagnostic component:', error);
      return undefined;
    }
    return data || undefined;
  }

  public async getWidget(messageWidget: ChatWidget): Promise<AffectedDiagnostic> {
    if (messageWidget.id !== 'guided-diagnosis') {
      console.error('[MessageService] Unsupported widget type:', messageWidget.id);
      return Promise.reject(new Error('Unsupported widget type'));
    }

    let carTypeId = 0;
    let errorCode = '';

    if (messageWidget.endpoint) {
      // TODO: Remove if after data migration
      // ====================================
      const split = messageWidget.endpoint.split('/');
      carTypeId = parseInt(split[split.length - 2], 10);
      errorCode = split[split.length - 1];
    } else {
      carTypeId = messageWidget.parameters?.carTypeId ?? 0;
      errorCode = messageWidget.parameters?.errorCode ?? '';
    }

    if (!carTypeId || !errorCode) {
      throw new Error('Given data is not sufficient to load widget');
    }

    const { data, error } = await this.api3Service.api.rmi.getDiagnosisForErrorCode(carTypeId, errorCode);

    if (error) {
      console.error('[MessageService] Error fetching widget data:', error);

      Sentry.captureException(toError(error), {
        tags: {
          location: 'MessageService',
          feature: 'ChatMessages',
          message: 'Failed to load chat messages.',
        },
      });

      throw error;
    }

    return data[0];
  }

  public async getWidgets(messageWidgets: ChatWidget[]): Promise<AffectedDiagnostic[] | undefined> {
    return Promise.all(messageWidgets.map((messageWidget) => this.getWidget(messageWidget)));
  }
}
